import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { eventBus } from '@/services/eventBus';
import type {
  Act,
  ActStatus,
  PlotAIReadiness,
  PlotOverview,
  PlotStructure,
  PlotStructureType,
  TensionLevel,
  TurningPoint
} from '@/types';
import { createEntityCache, generateEntityId } from '@/stores/_shared/entityStoreHelpers';

interface CreatePlotInput {
  overview?: Partial<PlotOverview>;
  acts?: Act[];
  notes?: string;
}

interface UpdatePlotInput {
  overview?: PlotOverview;
  notes?: string;
  acts?: Act[];
  aiReadiness?: PlotAIReadiness;
}

interface CreateActInput {
  title: string;
  purpose: string;
  protagonistGoal: string;
  opposition: string;
  actQuestion: string;
  startingEmotion: string;
  endingEmotion: string;
  internalConflict: string;
  externalConflict: string;
  actStakes: string;
  estimatedChapters: number;
  tensionLevel: TensionLevel;
  status: ActStatus;
  notes: string;
  color?: string;
  turningPoints?: TurningPoint[];
}

interface UpdateActInput {
  actId: string;
  payload: Partial<Omit<Act, 'id' | 'plotId' | 'number' | 'createdAt'>>;
}

type PlotCollection = Record<string, PlotStructure>;

interface PlotStoragePayload {
  version: string;
  lastUpdated: string;
  plot: PlotStructure | null;
}

const STORAGE_PREFIX = 'app_plot';
const STORAGE_VERSION = '1.0.0';

const storageKey = (manuscriptId: string): string => `${STORAGE_PREFIX}:${manuscriptId}`;

const cloneTurningPoint = (turningPoint: TurningPoint): TurningPoint => ({
  ...turningPoint
});

const cloneAct = (act: Act): Act => ({
  ...act,
  turningPoints: act.turningPoints.map((turningPoint) => cloneTurningPoint(turningPoint)),
  uiState: { ...act.uiState }
});

const clonePlot = (plot: PlotStructure): PlotStructure => ({
  ...plot,
  overview: { ...plot.overview },
  acts: plot.acts.map((act) => cloneAct(act)),
  aiReadiness: plot.aiReadiness ? { ...plot.aiReadiness } : undefined
});

const createDefaultOverview = (): PlotOverview => ({
  centralQuestion: '',
  genre: '',
  structureType: 'three-act',
  premise: '',
  stakes: '',
  theme: undefined,
  targetWordCount: 0
});

const resolveStructureType = (value: PlotStructureType | undefined): PlotStructureType => {
  if (!value) {
    return 'three-act';
  }
  return value;
};

const createPlotStructure = (manuscriptId: string, input: CreatePlotInput): PlotStructure => {
  const now = new Date().toISOString();
  const overviewOverrides = input.overview ?? {};
  const overview: PlotOverview = {
    ...createDefaultOverview(),
    ...overviewOverrides,
    structureType: resolveStructureType(overviewOverrides.structureType)
  };

  return {
    id: generateEntityId('plot'),
    manuscriptId,
    overview,
    acts: (input.acts ?? []).map((act) => cloneAct(act)),
    aiReadiness: undefined,
    createdAt: now,
    updatedAt: now,
    notes: input.notes ?? ''
  };
};

export const usePlotStore = defineStore('plot', () => {
  const plotsByManuscript = ref<PlotCollection>({});
  const activeManuscriptId = ref<string | null>(null);
  const isLoading = ref(false);
  const lastError = ref<string | null>(null);
  const cache = createEntityCache<PlotStructure | null>();

  const ensureState = (manuscriptId: string): void => {
    if (!plotsByManuscript.value[manuscriptId]) {
      plotsByManuscript.value[manuscriptId] = null as unknown as PlotStructure;
      delete plotsByManuscript.value[manuscriptId];
    }
  };

  const loadFromStorage = (manuscriptId: string): void => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey(manuscriptId));
      if (!raw) {
        plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: undefined as unknown as PlotStructure };
        delete plotsByManuscript.value[manuscriptId];
        return;
      }

      const payload = JSON.parse(raw) as PlotStoragePayload;
      if (payload.version !== STORAGE_VERSION) {
        return;
      }

      if (payload.plot) {
        plotsByManuscript.value = {
          ...plotsByManuscript.value,
          [manuscriptId]: clonePlot(payload.plot)
        };
      } else {
        delete plotsByManuscript.value[manuscriptId];
      }
    } catch (error) {
      console.error('[usePlotStore] Failed to read plot from storage', { manuscriptId, error });
    }
  };

  const persistToStorage = (manuscriptId: string): void => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const payload: PlotStoragePayload = {
        version: STORAGE_VERSION,
        lastUpdated: new Date().toISOString(),
        plot: plotsByManuscript.value[manuscriptId] ? clonePlot(plotsByManuscript.value[manuscriptId]) : null
      };

      if (payload.plot) {
        window.localStorage.setItem(storageKey(manuscriptId), JSON.stringify(payload));
      } else {
        window.localStorage.removeItem(storageKey(manuscriptId));
      }
    } catch (error) {
      console.error('[usePlotStore] Failed to persist plot to storage', { manuscriptId, error });
    }
  };

  const activePlot = computed<PlotStructure | null>(() => {
    if (!activeManuscriptId.value) {
      return null;
    }

    const manuscriptId = activeManuscriptId.value;
    const cacheKey = `${manuscriptId}:plot`;
    const cached = cache.get(cacheKey);
    if (cached !== undefined) {
      return cached ? clonePlot(cached) : null;
    }

    const plot = plotsByManuscript.value[manuscriptId];
    if (!plot) {
      cache.set(cacheKey, null);
      return null;
    }

    const cloned = clonePlot(plot);
    cache.set(cacheKey, cloned);
    return clonePlot(cloned);
  });

  const hasActivePlot = computed<boolean>(() => activePlot.value !== null);

  const actCount = computed<number>(() => {
    if (!activePlot.value) {
      return 0;
    }
    return activePlot.value.acts.length;
  });

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;
    if (!manuscriptId) {
      return;
    }

    ensureState(manuscriptId);
    loadFromStorage(manuscriptId);
    cache.invalidate(`${manuscriptId}:plot`);
  };

  const clearManuscript = (manuscriptId: string): void => {
    delete plotsByManuscript.value[manuscriptId];
    cache.invalidate(`${manuscriptId}:plot`);

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey(manuscriptId));
    }

    if (activeManuscriptId.value === manuscriptId) {
      activeManuscriptId.value = null;
    }
  };

  const createPlot = (input: CreatePlotInput): PlotStructure => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot create a plot without an active manuscript.');
    }
    const manuscriptId = activeManuscriptId.value;
    const plot = createPlotStructure(manuscriptId, input);

    plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: plot };
    cache.invalidate(`${manuscriptId}:plot`);
    persistToStorage(manuscriptId);

    return clonePlot(plot);
  };

  const updatePlot = (updates: UpdatePlotInput): PlotStructure => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot update a plot without an active manuscript.');
    }
    const manuscriptId = activeManuscriptId.value;
    const existing = plotsByManuscript.value[manuscriptId];
    if (!existing) {
      throw new Error('No plot data found for the active manuscript.');
    }

    const updated: PlotStructure = {
      ...existing,
      overview: updates.overview ? { ...updates.overview } : existing.overview,
      notes: updates.notes ?? existing.notes,
      acts: updates.acts ? updates.acts.map((act) => cloneAct(act)) : existing.acts,
      aiReadiness: updates.aiReadiness ? { ...updates.aiReadiness } : existing.aiReadiness,
      updatedAt: new Date().toISOString()
    };

    plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: updated };
    cache.invalidate(`${manuscriptId}:plot`);
    persistToStorage(manuscriptId);

    return clonePlot(updated);
  };

  const getNextActNumber = (plot: PlotStructure): number => {
    if (plot.acts.length === 0) {
      return 1;
    }
    const highest = plot.acts.reduce((max, act) => (act.number > max ? act.number : max), 0);
    return highest + 1;
  };

  const createAct = (input: CreateActInput): Act => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot create an act without an active manuscript.');
    }
    const manuscriptId = activeManuscriptId.value;
    const plot = plotsByManuscript.value[manuscriptId];
    if (!plot) {
      throw new Error('No plot data found for the active manuscript.');
    }
    const now = new Date().toISOString();
    const act: Act = {
      id: generateEntityId('act'),
      plotId: plot.id,
      number: getNextActNumber(plot),
      title: input.title,
      purpose: input.purpose,
      protagonistGoal: input.protagonistGoal,
      opposition: input.opposition,
      actQuestion: input.actQuestion,
      startingEmotion: input.startingEmotion,
      endingEmotion: input.endingEmotion,
      internalConflict: input.internalConflict,
      externalConflict: input.externalConflict,
      actStakes: input.actStakes,
      turningPoints: (input.turningPoints ?? []).map((turningPoint) => cloneTurningPoint(turningPoint)),
      estimatedChapters: input.estimatedChapters,
      tensionLevel: input.tensionLevel,
      status: input.status,
      uiState: {
        lastActiveTab: 'overview',
        isExpanded: false
      },
      createdAt: now,
      updatedAt: now,
      notes: input.notes,
      color: input.color
    };

    const updatedPlot: PlotStructure = {
      ...plot,
      acts: [...plot.acts, act],
      updatedAt: now
    };

    plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: updatedPlot };
    cache.invalidate(`${manuscriptId}:plot`);
    persistToStorage(manuscriptId);

    eventBus.emit('plot:actChanged', {
      manuscriptId,
      actId: act.id,
      action: 'created'
    });

    return cloneAct(act);
  };

  const updateAct = ({ actId, payload }: UpdateActInput): Act => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot update an act without an active manuscript.');
    }
    const manuscriptId = activeManuscriptId.value;
    const plot = plotsByManuscript.value[manuscriptId];
    if (!plot) {
      throw new Error('No plot data found for the active manuscript.');
    }
    const index = plot.acts.findIndex((act) => act.id === actId);
    if (index === -1) {
      throw new Error('Act not found.');
    }
    const existing = plot.acts[index];
    const updatedAct: Act = {
      ...existing,
      ...payload,
      turningPoints: payload.turningPoints
        ? payload.turningPoints.map((turningPoint) => cloneTurningPoint(turningPoint))
        : existing.turningPoints,
      uiState: payload.uiState ? { ...payload.uiState } : existing.uiState,
      updatedAt: new Date().toISOString()
    };
    const updatedActs = plot.acts.map((act) => (act.id === actId ? updatedAct : act));
    const updatedPlot: PlotStructure = {
      ...plot,
      acts: updatedActs,
      updatedAt: updatedAct.updatedAt
    };
    plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: updatedPlot };
    cache.invalidate(`${manuscriptId}:plot`);
    persistToStorage(manuscriptId);

    eventBus.emit('plot:actChanged', {
      manuscriptId,
      actId,
      action: 'updated'
    });

    return cloneAct(updatedAct);
  };

  const removeAct = (actId: string): void => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot remove an act without an active manuscript.');
    }
    const manuscriptId = activeManuscriptId.value;
    const plot = plotsByManuscript.value[manuscriptId];
    if (!plot) {
      throw new Error('No plot data found for the active manuscript.');
    }
    const remainingActs = plot.acts.filter((act) => act.id !== actId);
    if (remainingActs.length === plot.acts.length) {
      return;
    }
    const updatedPlot: PlotStructure = {
      ...plot,
      acts: remainingActs,
      updatedAt: new Date().toISOString()
    };
    plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: updatedPlot };
    cache.invalidate(`${manuscriptId}:plot`);
    persistToStorage(manuscriptId);

    eventBus.emit('plot:actChanged', {
      manuscriptId,
      actId,
      action: 'deleted'
    });
  };

  const setAiReadiness = (readiness: PlotAIReadiness | undefined): void => {
    if (!activeManuscriptId.value) {
      throw new Error('Cannot set AI readiness without an active manuscript.');
    }
    const manuscriptId = activeManuscriptId.value;
    const plot = plotsByManuscript.value[manuscriptId];
    if (!plot) {
      throw new Error('No plot data found for the active manuscript.');
    }
    const updatedPlot: PlotStructure = {
      ...plot,
      aiReadiness: readiness ? { ...readiness } : undefined,
      updatedAt: new Date().toISOString()
    };
    plotsByManuscript.value = { ...plotsByManuscript.value, [manuscriptId]: updatedPlot };
    cache.invalidate(`${manuscriptId}:plot`);
    persistToStorage(manuscriptId);

    eventBus.emit('aiReadiness:changed', {
      manuscriptId,
      system: 'plot',
      readiness: readiness?.overall ?? null
    });
  };

  const resetStore = (): void => {
    plotsByManuscript.value = {};
    activeManuscriptId.value = null;
    cache.invalidate();
    isLoading.value = false;
    lastError.value = null;
  };

  const getPlotByManuscript = (manuscriptId: string): PlotStructure | null => {
    const plot = plotsByManuscript.value[manuscriptId];
    if (!plot) {
      return null;
    }
    return clonePlot(plot);
  };

  return {
    activePlot,
    hasActivePlot,
    actCount,
    isLoading,
    lastError,
    setActiveManuscript,
    clearManuscript,
    createPlot,
    updatePlot,
    createAct,
    updateAct,
    removeAct,
    setAiReadiness,
    resetStore,
    getPlotByManuscript
  };
});
